// @ts-check
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {LOCATION_OPTIONS, mockLanguages} from '@/utils/constants/promotionConstants.js'
import {useEffect} from 'react'

export const usePromotionDisplay = () => {
  const {promotion, onResultChange, updatePromotion, promotionConfig} = usePromotion()
  const languagesFromHook = useFeatureFlagLanguages()

  const languages = languagesFromHook?.length > 0 ? languagesFromHook : mockLanguages

//   const routeTypeIdSelected = promotion?.routeTypeId
//     ? [{id: promotion.routeTypeId, displayText: promotion.routeTypeId}]
//     : [{id: 'Empty', displayText: 'Empty'}]

  // Set default bannerType if not already set
  useEffect(() => {
    if (!promotion.bannerType && promotionConfig?.bannerType?.length > 0) {
      const defaultBannerType = promotionConfig.bannerType[0]
      const updateData = {bannerType: defaultBannerType}

      if (defaultBannerType === 'clickable' && promotionConfig?.landingPage?.length > 0) {
        updateData.landingPage = promotionConfig.landingPage[0].value
      }

      onResultChange(updateData)
    }
  }, [promotion.bannerType, promotionConfig, onResultChange])

  const handleTextChange = (value, language, field) => {
    onResultChange({
      text: {
        ...promotion.text,
        [language]: {
          ...promotion.text?.[language],
          [field]: value,
        },
      },
    })
  }

  const handleDropdownChange = (field, value) => {
    if (field === 'displayOn') {
      return onResultChange({displayOn: Array.isArray(value) ? value.map(item => item.id) : []})
    }

    const selected = Array.isArray(value) && value.length > 0 ? value[0].id : null

    if (field === 'displayOrder') {
      return onResultChange({displayOrder: selected ? parseInt(selected) : 0})
    }

    if (field === 'bannerType') {
      const bannerType = selected ?? 'static'
      return onResultChange({bannerType, ...(bannerType === 'static' && {landingPage: ''})})
    }

    onResultChange({[field]: selected})
  }

  const handleDisplayFirstChange = checked => {
    updatePromotion(
      prev =>
        /** @type {any} */ ({
          ...prev,
          displayOrder: checked ? 1 : undefined,
        })
    )
  }

  const displayFirst = promotion.displayOrder === 1

  const bannerTypeOptions =
    promotionConfig?.bannerType?.map(type => ({
      displayText: type.charAt(0).toUpperCase() + type.slice(1),
      id: type,
    })) || []

//   const routeTypeIdOptions =
//     promotionConfig?.routeTypeId?.map(type => ({
//       displayText: type,
//       id: type,
//     })) || []

  const landingPageOptions =
    promotionConfig?.landingPage?.map(page => ({
      displayText: page.label,
      id: page.value,
    })) || []

  const getDisplayOnValue = () => {
    if (!promotion.displayOn || !Array.isArray(promotion.displayOn)) return []
    return promotion.displayOn.map(id => {
      const option = LOCATION_OPTIONS.find(opt => opt.id === id)
      return option ? {id, displayText: option.displayText} : {id, displayText: id}
    })
  }

  const getDisplayOrderValue = () => {
    if (!promotion.displayOrder) return []
    const str = promotion.displayOrder.toString()
    return [{id: str, displayText: str}]
  }

  const getBannerTypeValue = () => {
    if (!promotion.bannerType) {
      const first = promotionConfig?.bannerType?.[0]
      if (first) return [{id: first, displayText: first.charAt(0).toUpperCase() + first.slice(1)}]
      return [{id: 'static', displayText: 'Static'}]
    }
    return [
      {
        id: promotion.bannerType,
        displayText: promotion.bannerType.charAt(0).toUpperCase() + promotion.bannerType.slice(1),
      },
    ]
  }

  const getLandingPageValue = () => {
    if (!promotion.landingPage) return [{id: 'Empty', displayText: 'Empty'}]
    const option = promotionConfig?.landingPage?.find(page => page.value === promotion.landingPage)
    return option ? [{id: option.value, displayText: option.label}] : []
  }

  const showsOnPlaceholder = promotion?.displayOn
    ?.map(id => LOCATION_OPTIONS.find(opt => opt.id === id)?.displayText ?? '')
    .filter(Boolean)
    .join(', ')

  return {
    promotion,
    languages,
    displayFirst,
    bannerTypeOptions,
    landingPageOptions,
    showsOnPlaceholder,
    getDisplayOnValue,
    getDisplayOrderValue,
    getBannerTypeValue,
    getLandingPageValue,
    handleTextChange,
    handleDropdownChange,
    handleDisplayFirstChange,
  }
}
